import { z } from 'zod';
import { BaseErrorSchema } from '../../shared/error.schema';
import { CommittedRowSchema } from '../../shared/bulk-commit.schema';
import { ObjectIdSchema } from '../../shared/object-id.schema';
import { ProcessCategorySchema } from '../../entities/process/process.schema';
/**
 * Initialize Process Input Schema
 * Wizard row shape: the process owner is identified by email (resolved to a
 * user server-side), matching the Excel template. Department accepts
 * abbreviation, name, or ObjectId.
 */ export const InitializeProcessInputSchema = z.strictObject({
    name: z.string().min(1),
    description: z.string().min(1),
    department: z.string().min(1),
    ownerEmail: z.email(),
    // Optional accountable person by email; empty/absent = unset (read surfaces
    // fall back to the department's current manager).
    accountableEmail: z.email().or(z.literal('')).optional(),
    category: ProcessCategorySchema.nullable().optional()
});
/**
 * Update Process Input Schema
 * In-place edit of a wizard-seeded process, matched by `_id`
 * (ADR 0011, second amendment — extended to the optional steps)
 */ export const UpdateProcessInputSchema = InitializeProcessInputSchema.safeExtend({
    _id: ObjectIdSchema
});
/**
 * Initialize Add Processes Request Schema
 * POST /api/v2/initialize/processes
 * Re-enterable: rows whose (name, department) already exist are skipped.
 */ export const InitializeAddProcessesRequestSchema = z.object({
    processes: z.array(InitializeProcessInputSchema),
    // In-place edits of already-seeded rows, matched by `_id` (ADR 0011,
    // second amendment — extended to the optional steps).
    updates: z.array(UpdateProcessInputSchema).optional(),
    // Surgical deletion of already-seeded rows by `_id` (ADR 0011, third
    // amendment — extended to the optional steps).
    removals: z.array(ObjectIdSchema).optional()
}).refine((v)=>v.processes.length > 0 || !!v.updates?.length || !!v.removals?.length, {
    message: 'at least one of processes, updates or removals is required',
    path: [
        'processes'
    ]
}).refine((v)=>!v.updates?.some((update)=>v.removals?.includes(update._id)), {
    message: 'an id cannot appear in both updates and removals',
    path: [
        'removals'
    ]
});
/**
 * Initialize Add Processes Response Schema
 * One result per request `processes` row, in request order; skipped rows
 * carry the existing process's id so the wizard can edit it in place.
 */ export const InitializeAddProcessesResponseSchema = z.object({
    processes: z.array(CommittedRowSchema)
});
export const InitializeAddProcessesErrorSchema = BaseErrorSchema;

//# sourceMappingURL=initialize-add-processes.schema.js.map